import { z } from 'zod'

import {
  ExternalAccountKind,
  ExternalAccountType,
} from '../constants/organizationRelationshipKind'
import { managementUserSchema } from './managementUser'
import {
  organizationManagementUserCreateBodySchema,
  organizationSchema,
} from './organization'
import { addressSchema } from './system'
import { venueCreateArgsSchema, venueSchema } from './venue'

const optionalTrimmedEmailSchema = z
  .string()
  .optional()
  .superRefine((value, ctx) => {
    const normalizedEmail = value?.trim()

    if (!normalizedEmail) {
      return
    }

    if (!z.string().email().safeParse(normalizedEmail).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid email',
      })
    }
  })

const optionalTrimmedIdSchema = z.string().optional()

export const orgRegisterBodySchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(8),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    utm_source: z.string().nullish(),
    utm_medium: z.string().nullish(),
    utm_campaign: z.string().nullish(),
    utm_term: z.string().nullish(),
    utm_content: z.string().nullish(),
    accountType: z
      .nativeEnum(ExternalAccountType)
      .optional()
      .default(ExternalAccountType.VENUE),
    accountKind: z.nativeEnum(ExternalAccountKind).optional(),
    name: z.string().min(1),
    fullAddress: addressSchema.optional(),
    logo: z.string().url().optional().or(z.literal('')),
    venueData: venueCreateArgsSchema.omit({ organizationId: true }).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.accountType === ExternalAccountType.VENUE) {
      if (!data.fullAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'fullAddress is required for venue accounts',
          path: ['fullAddress'],
        })
      }
      if (!data.venueData) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'venueData is required for venue accounts',
          path: ['venueData'],
        })
      }
      return
    }

    if (!data.accountKind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'accountKind is required for external accounts',
        path: ['accountKind'],
      })
    }
  })

export const orgRegisterResponseSchema = z.object({
  token: z.string(),
  managementUser: managementUserSchema,
  organization: organizationSchema,
  venue: venueSchema.optional().nullable(),
})

export const adminOrgSetupBodySchema =
  organizationManagementUserCreateBodySchema
    .omit({
      email: true,
    })
    .extend({
      accountType: z
        .nativeEnum(ExternalAccountType)
        .optional()
        .default(ExternalAccountType.VENUE),
      email: optionalTrimmedEmailSchema,
      managementUserId: optionalTrimmedIdSchema,
    })
    .superRefine((data, ctx) => {
      const normalizedManagementUserId = data.managementUserId?.trim()
      const normalizedEmail = data.email?.trim()

      if (!normalizedManagementUserId && !normalizedEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'managementUserId or email is required',
          path: ['managementUserId'],
        })
      }

      if (data.accountType === ExternalAccountType.VENUE) {
        if (!data.fullAddress) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'fullAddress is required',
            path: ['fullAddress'],
          })
        }

        if (!data.venueData) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'venueData is required',
            path: ['venueData'],
          })
        }

        return
      }

      if (!data.accountKind) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'accountKind is required for external accounts',
          path: ['accountKind'],
        })
      }
    })

export const adminOrgSetupResponseSchema = z.object({
  managementUser: managementUserSchema,
  organization: organizationSchema,
  venue: venueSchema.nullish(),
})
